import React, { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm, useFieldArray } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Icon,
  Icons,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  Textarea,
  Textfield,
  ButtonTabs
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import useTags from 'hooks/useTags'
import {
  formatSharedChargeData,
  getCreditCardPaymentOptionsText,
  companyDeadlineDefaults,
  sharedInitialFormValues,
  setSharedFormData
} from 'templates/Charge/sharedCharge.utils'

import { useInCompanyForm, useSessionCookies } from 'components/CompanySelector'
import IconContainer from 'components/IconContainer/IconContainer'
import InputController from 'components/InputController'
import {
  MetricTagDialog,
  hasMetricTag,
  MetricTagOption
} from 'components/MetricTagDialog'
import ModalNewPaymentLink from 'components/Modal/ModalNewPaymentLink/components'
import { PaymentsTypeRef } from 'components/PaymentsType'
import SharedChargeFields from 'components/SharedChargeFields/SharedChargeFields'
import { Tag } from 'components/TagsAddInput/TagsAddInput'
import TagsPopover from 'components/TagsPopover/TagsPopover'
import UploadComponent from 'components/UploadComponent'

import { formatCents } from 'utils'
import { formatMoneyToRequest, onlyNumber } from 'utils/FormatMoney'
import { prepareFormDataWithTags } from 'utils/tags'
import { toastProps } from 'utils/types/toast-props'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import {
  DialogParams,
  FormPlansProps,
  FormPlansSchemaRequest
} from './FormProducts.interface'
import { FormProductParams, CombinedProductSchema } from './FormProducts.schema'
import {
  formCadence,
  hasCadenceChanged,
  productCadence
} from './utils/cadence.utils'

const iconsToShow = [
  'add',
  'arrowDownward',
  'arrowCircleLeft',
  'arrowCircleRight',
  'arrowUpward',
  'assignment',
  'accountBalance',
  'search',
  'cancel',
  'supervisorAccount',
  'event',
  'delete',
  'download',
  'edit',
  'receipt',
  'filterAlt',
  'gavel',
  'info',
  'manageAccounts',
  'paid',
  'update',
  'settings',
  'checkCircle',
  'accountCircle',
  'barChart',
  'visibility',
  'watchLater',
  'mail',
  'sms',
  'lock',
  'person',
  'noteAdd',
  'task',
  'web',
  'archive',
  'priceChange',
  'arrowForward',
  'attachMoney',
  'workspacePremium',
  'sentimentDissatisfied',
  'thumbUpAlt',
  'thumbDownAlt',
  'forwardToInbox',
  'contactMail',
  'creditCardChecked',
  'acUnit',
  'beachAccess',
  'bookmark',
  'buildCircle',
  'castle',
  'cellTower',
  'chair',
  'desktopMac',
  'favorite',
  'public',
  'eventRepeat',
  'link',
  'phone',
  'help',
  'autoAwesome',
  'insights',
  'send',
  'faGift',
  'faHelp',
  'faBell',
  'faChart',
  'faBullhorn',
  'faRectangleList',
  'faStore',
  'faCoins',
  'faChartLine',
  'faCreditCard',
  'faShieldHeart',
  'faCircleArrowUp',
  'faCirclePlus',
  'faCircleMinus',
  'faCircleQuestion',
  'faRightLeft',
  'faArrowRightFromBracket',
  'faArrowRightToBracket',
  'faShare',
  'faHouse',
  'faStar',
  'faHeart',
  'faUsers',
  'faFileSignature',
  'faReceipt',
  'faRepeat',
  'faLinkedin'
]

const initialFormValues = {
  ...sharedInitialFormValues,
  // Sobrescrever apenas os valores específicos do form de produtos
  chargeType: 0,
  icon: 'faStar',
  dueType: 'immediate_charge',
  proratedCharge: false,
  // O plano define a própria frequência; não há composição de onde derivá-la.
  frequency: 1
}

const FormProducts: React.FC<FormPlansProps> = ({
  handleRefreshPlans,
  setIsOpenDialog,
  product,
  isDuplicate,
  setValueFromPaymentLinkCreation,
  onSuccessFunction,
  onProductCreated,
  companySelector
}) => {
  const isEditMode = !!product && !isDuplicate
  const [confirmEdit, setConfirmEdit] = useState<DialogParams>({
    open: false,
    dataForm: {} as FormProductParams
  })

  // Impacto de mudar a cadência do plano nas recorrências que já o usam. `null` = a
  // cadência não mudou (ou ainda não foi consultada), e o diálogo segue o texto de sempre.
  const [cadenceImpact, setCadenceImpact] = useState<{
    total: number
    applicable: number
    skipped: number
  } | null>(null)

  /**
   * A cadência do item é materializada na criação, então editar o plano NÃO alcança
   * recorrências existentes por conta própria — de propósito, para não re-cadenciar
   * contratos vivos em silêncio. Aqui perguntamos se o merchant quer alcançar.
   *
   * Consulta com a frequência PROSPECTIVA (a que ele acabou de escolher), antes de salvar.
   */
  const fetchCadenceImpact = async (dataForm: FormProductParams) => {
    if (!isEditMode || !product?.uuid) return null

    // A API manda a frequência pelo nome do enum e o form guarda o inteiro; ciclos acima
    // de 12 ficam como "Outros" (13). Normaliza os dois lados antes de comparar.
    const current = productCadence(product as any)
    const next = formCadence(dataForm as any)

    if (!hasCadenceChanged(current, next)) return null

    try {
      const params = new URLSearchParams()
      params.set('frequency', String(next.frequency))
      params.set('frequency_type', next.unit)
      params.set('frequency_number', String(next.count))
      params.set('total_cycles', String(next.totalCycles))

      const response = await DataService({
        type: 'GET',
        url: `/v1/products/${product.uuid}/cadence_impact?${params.toString()}`
      })

      const record = response?.data?.record
      return record && record.total > 0 ? record : null
    } catch {
      // Impacto é informativo: se a consulta falhar, o merchant ainda consegue salvar
      // (sem propagar), que é o comportamento conservador.
      return null
    }
  }
  const [confirmNew, setConfirmNew] = useState<DialogParams>({
    open: false,
    dataForm: {} as FormProductParams
  })

  const [dialogNewProduct, setDialogNewProduct] = useState<DialogParams>({
    open: false,
    dataForm: {} as FormProductParams
  })

  const [isOpenNewPaymentLinkModal, setIsOpenNewPaymentLinkModal] =
    useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(false)

  const uploadRef = useRef<UploadComponent>({} as UploadComponent)
  const paymentsTypeRef = useRef<PaymentsTypeRef>(null)

  const [createdProductUuid, setCreatedProductUuid] = useState<string>('')

  // Estados para SharedChargeFields
  const [isCharge, setIsCharge] = useState<boolean>(false)
  const [isUnique, setIsUnique] = useState<boolean>(true)
  // Hook para gerenciar tags
  const { tags: availableTags, createTag } = useTags()

  // State for metric tag dialog
  const [metricTagDialog, setMetricTagDialog] = useState<{
    open: boolean
    dataForm: FormProductParams | null
    isEdit: boolean
  }>({
    open: false,
    dataForm: null,
    isEdit: false
  })

  // Icon/Image selection states
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [iconSelectionTab, setIconSelectionTab] = useState<'icon' | 'image'>(
    'icon'
  )
  const [selectedIcon, setSelectedIcon] = useState<string>('star')
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const [uploadedFileData, setUploadedFileData] = useState<{
    id: string
    preview: string
  } | null>(null)

  const [externalUploadState, setExternalUploadState] = useState<any[]>([])

  const handleCloseCreatePaymentLink = () => {
    !!setIsOpenDialog && setIsOpenDialog()
  }
  const handleOpenNewPaymentLinkModal = () => {
    setIsOpenNewPaymentLinkModal(true)
  }

  const inCompanyForm = useInCompanyForm()
  const sessionCookies = useSessionCookies()
  const {
    control,
    watch,
    register,
    setValue,
    trigger,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm<FormProductParams>({
    defaultValues: {
      ...initialFormValues,
      // SC-10999: criado para outra empresa do grupo, valem os prazos dela
      ...(inCompanyForm && companyDeadlineDefaults(sessionCookies))
    } as any,
    resolver: yupResolver(CombinedProductSchema),
    context: { isProductOrLink: true, isLink: false },
    mode: 'onChange'
  })

  // ✅ useFieldArray para descontos (após useForm)
  const { replace: replaceDiscounts } = useFieldArray({
    control,
    name: 'discounts'
    // ✅ Removido defaultValue - será inicializado pelos useEffects
  })

  // Sincronizar chargeType com isUnique (bidirecional)
  useEffect(() => {
    const chargeType = watch('chargeType')
    setIsUnique(chargeType === 0) // 0 = única, 1 = recorrente
  }, [watch('chargeType')])

  useEffect(() => {
    setValue('chargeType', isUnique ? 0 : 1)
  }, [isUnique, setValue])

  const onSubmit = async (
    dataForm: FormProductParams,
    propagateToRecurrences = false
  ) => {
    setLoading(true)
    const id = toast.loading(
      isEditMode ? 'Editando plano...' : 'Adicionando plano...'
    )

    // Usar formatSharedChargeData para os campos compartilhados
    const sharedData = formatSharedChargeData({
      dataForm: dataForm as any,
      isUnique,
      isCharge,
      isEdit: isEditMode,
      isProductOrLink: true,
      isLink: false
    })

    // Extrair e converter campos que precisam de conversão de tipo
    const { due_day, total_cycles, frequency, ...restSharedData } = sharedData

    const data: FormPlansSchemaRequest = {
      // Spread dos dados compartilhados
      ...(restSharedData as any),
      // Conversões de tipo necessárias
      ...(due_day && {
        due_day: Number(due_day)
      }),
      ...(total_cycles !== undefined &&
        total_cycles !== null && {
          total_cycles: Number(total_cycles)
        }),
      // Semanal é o enum 0: testar por truthiness descartaria a frequência.
      ...(frequency !== undefined &&
        frequency !== null && {
          frequency: Number(frequency)
        }),
      // Dados específicos do form de produtos
      ...(uploadedFileData && {
        document_uuid: uploadedFileData.id
      }),
      name: dataForm?.namePlan as string,
      icon: uploadedFileData ? undefined : (dataForm?.icon as string),
      amount: formatMoneyToRequest(dataForm?.amount?.toString() || '0'),
      ...(dataForm?.description && {
        description: dataForm?.description
      }),
      ...(dataForm?.billingDescription && {
        billing_description: dataForm?.billingDescription
      }),
      // Replicar a nova cadência/janela nas recorrências que já usam este plano. Só vai
      // quando o merchant escolhe explicitamente no diálogo — o backend ignora o campo
      // quando a cadência não mudou.
      ...(propagateToRecurrences && { propagate_to_recurrences: true })
    }

    const response = await DataService({
      url: `/v1/products${isEditMode ? `/${product?.uuid}` : ''}`,
      type: isEditMode ? 'PATCH' : 'POST',
      data
    })

    const errorMessage: string =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    if (response?.status === 201 || response?.status === 200) {
      const createdUuid = response.data.records.uuid
      setCreatedProductUuid(createdUuid)

      !!handleRefreshPlans && handleRefreshPlans()

      // If onSuccessFunction is provided (creating from ItemRow Autocomplete), call onProductCreated first, wait for it, then call onSuccessFunction
      if (onSuccessFunction) {
        // Call onProductCreated callback first if provided (for new products)
        if (onProductCreated && !isEditMode) {
          onProductCreated(createdUuid)
          // Give time for the callback to process the product
          await new Promise((resolve) => setTimeout(resolve, 300))
        }

        // Then call onSuccessFunction
        await onSuccessFunction()

        // Finally close the modal
        !!setIsOpenDialog && setIsOpenDialog()
      } else {
        // Normal flow for other cases
        if (isEditMode) {
          !!setIsOpenDialog && setIsOpenDialog()
        } else {
          !!setDialogNewProduct && setDialogNewProduct({ open: true, dataForm })
        }

        // Call onProductCreated callback if provided (for other cases)
        if (onProductCreated && !isEditMode) {
          onProductCreated(createdUuid)
        }
      }
    }

    if (setValueFromPaymentLinkCreation) {
      setValueFromPaymentLinkCreation('productUuid', response.data.records.uuid)
    }

    setConfirmEdit({ open: false })
    setConfirmNew({ open: false })
    setLoading(false)
  }

  useEffect(() => {
    if (!product) {
      // Inicializar vazio - o Discounts adiciona a primeira linha quando o switch é ligado
      replaceDiscounts([])
      return
    }
    // Se chegou aqui, temos um produto
    const parseAmount = onlyNumber(formatCents(product?.amount) || '0')

    // Primeiro, determinar se é único ou recorrente baseado no produto
    const productIsUnique = !product?.frequency || product?.totalCycles === 1
    setIsUnique(productIsUnique)
    setValue('chargeType', productIsUnique ? 0 : 1)

    // Aguardar um tick para que os useEffects de chargeType sejam executados
    setTimeout(() => {
      // Usar a função utilitária para setar dados compartilhados
      setSharedFormData(
        {
          discounts: product?.discounts,
          discountPolicy: product?.discountPolicy,
          interestPolicy: product?.interestPolicy,
          interestFee: product?.interestFee || '1.0',
          interestFine: product?.interestFine || '2.0',
          frequency: product?.frequency,
          frequency_type: product?.frequency_type,
          frequency_number: product?.frequency_number,
          totalCycles: product?.totalCycles,
          dueDay: product?.dueDay,
          paymentType: product?.paymentType,
          paymentTypeDetails: product?.paymentTypeDetails,
          nfseScheduled: product?.nfseScheduled,
          nfsePolicy: product?.nfsePolicy,
          nfseIssuerUuid: product?.nfseIssuerUuid,
          hasBonus: product?.hasBonus,
          bonusSettings: product?.bonusSettings,
          metadata: (product as any)?.metadata,
          // Dados de negativação
          negativation: product?.negativation,
          // Campo de expiração para produtos
          limitDaysForPayment: product?.limit_days_for_payment,
          // Campos específicos de produtos (TypesCharges)
          chargeType: product?.chargeType,
          dueType: product?.dueType,
          proratedCharge: product?.proratedCharge,
          issueDays: product?.issue_days,
          issueBillingType: product?.issue_billing_type,
          hasIssueDays: !!product?.issue_days,
          // Dados de reajuste agendado
          adjustmentScheduled: (product as any)?.adjustment_scheduled
        },
        setValue,
        replaceDiscounts,
        !productIsUnique, // isRecurrence = !productIsUnique
        undefined,
        true // isProductOrLink
      )

      // Depois setar campos específicos do produto (sem sobrescrever os compartilhados)
      setValue('amount', parseAmount)
      setValue('description', product?.description ? product?.description : '')
      setValue(
        'billingDescription',
        product?.billingDescription ? product?.billingDescription : ''
      )
      setValue('icon', product?.icon || 'acUnit')
      setValue('namePlan', product?.name)
      setValue('documentUuid', product?.documentFile?.uuid)
    }, 0)

    // Configurar imagem/ícone baseado nos dados do produto
    if (product?.documentFile?.thumbnail_url) {
      // Se tem imagem, usar imagem
      setUploadedFileData({
        id: product.documentFile.uuid,
        preview: product.documentFile.thumbnail_url
      })
      setSelectedImage(product.documentFile.thumbnail_url)
      setSelectedIcon('')
      setIconSelectionTab('image')
    } else if (product?.icon) {
      // Se não tem imagem mas tem ícone, usar ícone
      setSelectedIcon(product.icon)
      setUploadedFileData(null)
      setSelectedImage(null)
      setIconSelectionTab('icon')
    } else {
      // Fallback para ícone padrão
      setSelectedIcon('star')
      setUploadedFileData(null)
      setSelectedImage(null)
      setIconSelectionTab('icon')
    }
  }, [product])

  useEffect(() => {
    if (externalUploadState.length > 0) {
      const uploadedFile = externalUploadState[externalUploadState.length - 1]

      if (externalUploadState.length > 1 && uploadRef.current) {
        const latestFile = externalUploadState[externalUploadState.length - 1]

        if (typeof uploadRef.current.setState === 'function') {
          uploadRef.current.setState({
            uploadedFiles: [latestFile]
          })
        }

        setExternalUploadState([latestFile])
        return
      }

      if (uploadedFile.preview && !uploadedFile.uploaded) {
        setSelectedImage(uploadedFile.preview)
        setSelectedIcon('')

        setUploadedFileData(null)
        setValue('documentUuid', undefined)
      }

      if (uploadedFile.uploaded) {
        setUploadedFileData({
          id: uploadedFile.id,
          preview: uploadedFile.preview
        })
        setValue('documentUuid', uploadedFile.id)
        setSelectedImage(uploadedFile.preview)
        setSelectedIcon('')

        setAnchorEl(null)
      }
    } else {
      setValue('documentUuid', undefined)
    }
  }, [externalUploadState])

  useEffect(() => {
    if (uploadedFileData) {
      setValue('documentUuid', uploadedFileData.id)
      setSelectedImage(uploadedFileData.preview)
      setSelectedIcon('')
    } else {
      setValue('documentUuid', undefined)
    }
  }, [uploadedFileData])

  // Converter tags quando product ou availableTags mudarem
  useEffect(() => {
    if (product && availableTags.length > 0) {
      const formDataWithTags = prepareFormDataWithTags(product, availableTags)

      // Se há tags para converter, atualizar o formulário
      if (formDataWithTags.tags && formDataWithTags.tags.length > 0) {
        setValue('tags', formDataWithTags.tags)
      }
    }
  }, [product, availableTags, setValue])

  const handleSubmitForm: SubmitHandler<FormProductParams> = async (
    dataForm,
    event
  ) => {
    event?.preventDefault()

    // Verificar especificamente os switches do cartão de crédito (novo formato)
    const creditCard = dataForm.payment_type_details?.credit_card
    // Trigger validation before proceeding
    const isFormValid = await trigger()

    if (!isFormValid) {
      toast.error('Corrija os erros no formulário antes de continuar')
      return
    }

    if (
      creditCard?.enabled &&
      creditCard?.max_installments &&
      creditCard.max_installments > 1
    ) {
      const hasAtLeastOneSwitch = !!(
        creditCard.credit_card_installments?.enabled ||
        creditCard.credit_card_recurring?.enabled ||
        creditCard.credit_card?.enabled
      )

      if (!hasAtLeastOneSwitch) {
        const isRecurrence = false // Produtos podem ser únicos ou recorrentes, mas aqui estamos validando apenas parcelas > 1
        const hasInstallments = true // Já estamos dentro da condição creditCard.installments > 1
        const errorMessage = getCreditCardPaymentOptionsText(
          isRecurrence,
          hasInstallments
        )

        toast.error(errorMessage)
        paymentsTypeRef.current?.scrollToCreditCard()
        return
      }
    }

    if (selectedIcon && !uploadedFileData) {
      setValue('icon', selectedIcon)
    }

    // Verificar se já tem tag MRR ou AVULSA
    const currentTags = (dataForm.tags as Tag[]) || []
    if (!hasMetricTag(currentTags)) {
      // Não tem tag de métrica, mostrar dialog para escolher
      setMetricTagDialog({
        open: true,
        dataForm,
        isEdit: isEditMode
      })
      return
    }

    // Já tem tag de métrica, seguir fluxo normal
    if (isEditMode) {
      setCadenceImpact(await fetchCadenceImpact(dataForm))
      setConfirmEdit({ open: true, dataForm })
    } else {
      setConfirmNew({ open: true, dataForm })
    }
  }

  // Handler para quando usuário confirma a tag de métrica
  const handleMetricTagConfirm = async (
    selectedOption: MetricTagOption,
    tag: Tag | null
  ) => {
    if (!metricTagDialog.dataForm) return

    let updatedDataForm = { ...metricTagDialog.dataForm }
    const currentTags = (updatedDataForm.tags as Tag[]) || []

    if (selectedOption !== 'NONE') {
      if (tag) {
        // Tag já existe, apenas adicionar
        updatedDataForm = {
          ...updatedDataForm,
          tags: [...currentTags, tag]
        }
        setValue('tags', [...currentTags, tag])
      } else {
        // Tag não existe, criar primeiro
        try {
          const tagName = selectedOption === 'MRR' ? 'MRR' : 'AVULSA'
          const newTag = await createTag(tagName)
          updatedDataForm = {
            ...updatedDataForm,
            tags: [...currentTags, newTag]
          }
          setValue('tags', [...currentTags, newTag])
        } catch (error) {
          toast.error('Erro ao criar tag de métrica')
          setMetricTagDialog({ open: false, dataForm: null, isEdit: false })
          return
        }
      }
    }

    // Fechar dialog de métrica e abrir dialog de confirmação
    setMetricTagDialog({ open: false, dataForm: null, isEdit: false })

    if (metricTagDialog.isEdit) {
      setConfirmEdit({ open: true, dataForm: updatedDataForm })
    } else {
      setConfirmNew({ open: true, dataForm: updatedDataForm })
    }
  }

  // Calcular total_cycles para o dialog de métrica
  const getEffectiveTotalCycles = (): number => {
    if (!metricTagDialog.dataForm) return 0
    const formData = metricTagDialog.dataForm
    if (formData.totalCycles === 13 && formData.totalCyclesCustom) {
      return parseInt(formData.totalCyclesCustom, 10) || 0
    }
    return formData.totalCycles || 0
  }

  return (
    <>
      <ModalBody
        component="form"
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(handleSubmitForm, toastFirstError)(e)
          return false
        }}
      >
        <ModalHeader onClose={setIsOpenDialog as unknown as () => void}>
          <Stack direction="row" gap={2} alignItems="center">
            <IconContainer icon={isEditMode ? 'edit' : 'star'} />
            <Typography variant="mdBold">
              {isEditMode ? 'Editar' : isDuplicate ? 'Duplicar' : 'Novo'} plano
            </Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          {companySelector}
          <Stack spacing={2}>
            <Stack direction="row" gap={0.5}>
              <Typography variant="mdBold">Nome e imagem seu plano</Typography>
              <Tooltip
                arrow
                placement="right"
                title="Você pode escolher um ícone ou uma imagem para representar seu plano. Caso não escolha nenhum, usaremos um ícone padrão."
              >
                <div>
                  <Icon
                    icon="info"
                    sx={{
                      color: theme.palette.neutral[700],
                      width: '20px'
                    }}
                  />
                </div>
              </Tooltip>
            </Stack>

            <Box display="flex" alignItems="center" gap={2}>
              <Box
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                  width: 52,
                  height: 52,
                  border: '1px solid',
                  borderColor:
                    selectedIcon || selectedImage
                      ? theme.palette.neutral[600]
                      : theme.palette.neutral[600],
                  borderRadius: 2,
                  display: 'flex',
                  mt: 0.4,
                  position: 'relative',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: theme.palette.neutral[900],
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.neutral[200]
                  }
                }}
              >
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 1
                    }}
                  />
                ) : (
                  <Icon
                    icon={selectedIcon as Icons}
                    colorSX={
                      selectedIcon
                        ? theme.palette.neutral[200]
                        : theme.palette.neutral[600]
                    }
                    size={3}
                  />
                )}

                <Stack
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    zIndex: 2,
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    bgcolor: 'white',
                    bottom: -10,
                    right: -8,
                    cursor: 'pointer',
                    border: `1px solid ${theme.palette.neutral[400]}`
                  }}
                >
                  <Icon
                    sx={{
                      width: '14px',
                      height: '13px',
                      color: theme.palette.neutral[400]
                    }}
                    icon="edit"
                  />
                </Stack>
              </Box>

              <InputController name="namePlan" control={control}>
                <Textfield
                  id="plan-name"
                  label="Nome"
                  placeholder="Digite aqui o nome do plano"
                  inputProps={{ maxLength: 255 }}
                />
              </InputController>
            </Box>

            {/* Popover for icon/image selection */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              PaperProps={{
                sx: {
                  p: 2,
                  width: 320,
                  maxHeight: 400
                }
              }}
            >
              <Stack spacing={2}>
                <ButtonTabs
                  value={iconSelectionTab}
                  tabs={[
                    { label: 'Ícone', value: 'icon' },
                    { label: 'Imagem', value: 'image' }
                  ]}
                  onChange={(_, value) =>
                    setIconSelectionTab(value as 'icon' | 'image')
                  }
                />

                {iconSelectionTab === 'icon' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: 1,
                      maxHeight: 200,
                      overflowY: 'auto'
                    }}
                  >
                    {iconsToShow.map((icon) => (
                      <Box
                        key={icon}
                        onClick={() => {
                          setSelectedIcon(icon)
                          setSelectedImage(null)
                          setUploadedFileData(null)
                          setValue('icon', icon)
                          setAnchorEl(null)
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          border: '1px solid',
                          borderColor:
                            selectedIcon === icon ? 'primary.main' : 'grey.300',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          backgroundColor:
                            selectedIcon === icon
                              ? 'primary.50'
                              : 'transparent',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'primary.50'
                          }
                        }}
                      >
                        <Icon icon={icon as Icons} size={2} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Stack spacing={0.5}>
                    <UploadComponent
                      type="announcement_portals"
                      maxFiles={uploadedFileData ? 2 : 1} // Allow 2 files when replacing
                      subTitle={
                        uploadedFileData
                          ? 'Arraste ou faça upload para substituir a imagem atual.'
                          : 'Arraste ou faça upload da sua imagem para anexá-los.'
                      }
                      docType="product_image"
                      title={
                        uploadedFileData
                          ? 'Substituir imagem'
                          : 'Envie sua imagem'
                      }
                      ref={uploadRef}
                      accept={{
                        'image/jpeg': ['.jpeg', '.jpg'],
                        'image/png': ['.png'],
                        'image/webp': ['.webp']
                      }}
                      disableList
                      setStateExternal={setExternalUploadState}
                    />

                    {/* Manual file listing */}
                    {uploadedFileData && (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: theme.palette.neutral[700],
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <Box
                          component="img"
                          src={uploadedFileData.preview}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="smBold">
                            Imagem carregada
                          </Typography>
                        </Box>
                        <Icon
                          icon="delete"
                          sx={{
                            cursor: 'pointer',
                            color: theme.palette.neutral[400],
                            '&:hover': {
                              color: theme.palette.error.main
                            }
                          }}
                          onClick={() => {
                            setUploadedFileData(null)
                            setSelectedImage(null)
                            // Reset uploadRef if it exists and has setState method
                            try {
                              if (
                                uploadRef.current &&
                                typeof uploadRef.current.setState === 'function'
                              ) {
                                uploadRef.current.setState({
                                  uploadedFiles: []
                                })
                              }
                            } catch (error) {
                              //
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Popover>

            <InputController name="description" control={control}>
              <Textarea
                id="plan-description"
                label="Descrição (opcional)"
                placeholder="Digite aqui a descrição do plano"
                style={{
                  height: '90px',
                  resize: 'vertical',
                  maxHeight: '50vh'
                }}
              />
            </InputController>

            <Typography variant="mdBold">Valor</Typography>

            <InputController name="amount" control={control} sx={{ mb: 2 }}>
              <Textfield
                id="plan-amount"
                customStartAdornment={
                  <Typography mr="6px" variant="smLight">
                    R$
                  </Typography>
                }
                label="Qual o valor do plano?"
                placeholder="R$ 0,00"
                mask="money"
                startAdornment={true}
              />
            </InputController>

            <SharedChargeFields
              control={control as any}
              watch={watch as any}
              setValue={setValue as any}
              trigger={trigger as any}
              errors={errors as any}
              register={register}
              clearErrors={clearErrors as any}
              isCharge={isCharge}
              setIsCharge={setIsCharge}
              isUnique={isUnique}
              setIsUnique={setIsUnique}
              isEdit={!!product}
              editType={isUnique ? 'charge' : 'recurrence'}
              isProductOrLink={true}
              hiddenSections={['composition', 'itemsOnDescription']}
              externalReplaceDiscounts={replaceDiscounts}
              paymentsTypeRef={paymentsTypeRef}
            />
          </Stack>
        </ModalContent>

        <ModalFooter>
          <Stack direction="row" alignItems="center" gap={2}>
            {/* Ícone de Tags */}
            <TagsPopover control={control} name="tags" maxTags={10} />

            <Button
              variantButton="primary"
              type="button"
              onClick={(e) => {
                e.preventDefault()
                handleSubmit(handleSubmitForm, toastFirstError)(e)
                return false
              }}
              disabled={loading}
              loading={loading}
            >
              {isEditMode ? 'Atualizar plano' : 'Criar plano'}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalBody>

      {/* Confirmar Edição. Quando a cadência mudou e há recorrências usando o plano, o
          diálogo deixa de ser um "ok" e vira uma escolha: alcançar ou não os contratos
          que já existem. As recorrências de frequência incompatível são sempre puladas —
          o item passaria a vencer entre dois ciclos, sem cobrança onde existir. */}
      <Dialog
        icon="edit"
        title="Editar Plano"
        description={
          cadenceImpact
            ? `A frequência deste plano mudou e ${cadenceImpact.total} ${
                cadenceImpact.total === 1
                  ? 'recorrência já usa'
                  : 'recorrências já usam'
              } ele. Deseja aplicar a nova frequência ${
                cadenceImpact.applicable === cadenceImpact.total
                  ? 'nelas também'
                  : `nas ${cadenceImpact.applicable} compatíveis`
              }, ou só nas novas?${
                cadenceImpact.skipped > 0
                  ? ` ${cadenceImpact.skipped} ${
                      cadenceImpact.skipped === 1
                        ? 'será ignorada por ter frequência incompatível'
                        : 'serão ignoradas por terem frequência incompatível'
                    }.`
                  : ''
              }`
            : 'Confirma editar plano? Essa edição só valerá para novas recorrências associadas ao plano.'
        }
        setIsOpenDialog={() => {
          setConfirmEdit({ open: false, dataForm: undefined })
          setCadenceImpact(null)
        }}
        isOpenDialog={confirmEdit.open}
        cancelButton
        // Com a escolha de alcançar as recorrências são três botões: na largura padrão
        // (312px) o "Cancelar" ficava cortado e o "Aplicar em N" quebrava em duas linhas.
        {...(cadenceImpact &&
          cadenceImpact.applicable > 0 && {
            PaperProps: { sx: { maxWidth: '480px', width: '100%' } }
          })}
      >
        {cadenceImpact && cadenceImpact.applicable > 0 && (
          <Button
            fullWidth
            variantButton="neutral"
            onClick={(e) => {
              e.preventDefault()
              onSubmit(
                confirmEdit?.dataForm || ({} as FormProductParams),
                false
              )
              return false
            }}
            disabled={loading}
            loading={loading}
          >
            Só nas novas
          </Button>
        )}
        <Button
          fullWidth
          onClick={(e) => {
            e.preventDefault()
            onSubmit(
              confirmEdit?.dataForm || ({} as FormProductParams),
              Boolean(cadenceImpact && cadenceImpact.applicable > 0)
            )
            return false
          }}
          disabled={loading}
          loading={loading}
        >
          {cadenceImpact && cadenceImpact.applicable > 0
            ? `Aplicar em ${cadenceImpact.applicable}`
            : 'Editar'}
        </Button>
      </Dialog>

      {/* Confirmar Novo */}
      <Dialog
        icon="add"
        title="Novo plano"
        description="Confirma criar novo plano?"
        setIsOpenDialog={() =>
          setConfirmNew({ open: false, dataForm: undefined })
        }
        isOpenDialog={confirmNew.open}
        cancelButton
      >
        <Button
          id="confirm-create-plan"
          fullWidth
          onClick={(e) => {
            e.preventDefault()
            onSubmit(confirmNew.dataForm || ({} as FormProductParams))
            return false
          }}
          disabled={loading}
          loading={loading}
        >
          Criar
        </Button>
      </Dialog>

      {/* Seu plano foi criado Edição */}
      <Dialog
        icon="checkCircle"
        iconColor={theme.palette.primary.main}
        title="Seu plano foi criado!"
        description="Você quer gerar um link de contratação para esse plano?"
        setIsOpenDialog={() => {
          setDialogNewProduct({ open: false, dataForm: undefined })
          !!setIsOpenDialog && setIsOpenDialog()
        }}
        isOpenDialog={dialogNewProduct.open}
      >
        <Box display="flex" gap="8px" width="100%">
          <Button
            fullWidth
            onClick={(e) => {
              e.preventDefault()
              setDialogNewProduct({ open: false, dataForm: undefined })
              !!setIsOpenDialog && setIsOpenDialog()
              return false
            }}
            disabled={loading}
            variantButton="neutral"
          >
            Agora não
          </Button>
          <Button
            fullWidth
            onClick={() => {
              handleOpenNewPaymentLinkModal()
              setDialogNewProduct({ open: false, dataForm: undefined })
            }}
            disabled={loading}
            loading={loading}
          >
            Criar link
          </Button>
        </Box>
      </Dialog>

      {/* Dialog para seleção de tag de métrica (MRR/AVULSA) */}
      <MetricTagDialog
        isOpen={metricTagDialog.open}
        onClose={() =>
          setMetricTagDialog({ open: false, dataForm: null, isEdit: false })
        }
        onConfirm={handleMetricTagConfirm}
        totalCycles={getEffectiveTotalCycles()}
        availableTags={availableTags}
        loading={loading}
      />

      <ModalNewPaymentLink
        isOpen={isOpenNewPaymentLinkModal}
        handleClose={() => setIsOpenNewPaymentLinkModal(false)}
        isEdit={false}
        handleCloseCreatePaymentLink={handleCloseCreatePaymentLink}
        productUuid={createdProductUuid}
        isDuplication={false}
      />
    </>
  )
}

export default FormProducts
